/**
 * PRD-018 §3.11: the one shortcut table. Handlers and the `?` sheet both read it, so the sheet can
 * never drift from what the keys do. Chords are `g` then a letter; single keys are suppressed in fields.
 */
export type ShortcutId = 'palette' | 'palette_slash' | 'sheet' | 'tree_toggle' | 'period_prev' | 'period_next' | 'period_picker' | 'go_home' | 'go_my_work' | 'go_notifications' | 'go_team' | 'go_work' | 'go_agent_jobs' | 'new_task' | 'list_down' | 'list_up' | 'list_open' | 'list_complete' | 'list_assign'

export interface Shortcut { id: ShortcutId, keys: string, label: string, group: 'Global' | 'Navigate' | 'Period' | 'Create' | 'Lists' }

export const SHORTCUTS: readonly Shortcut[] = [
  { id: 'palette', keys: 'mod+k', label: 'Open the command palette', group: 'Global' },
  { id: 'palette_slash', keys: '/', label: 'Open the palette (outside a field)', group: 'Global' },
  { id: 'sheet', keys: 'shift+/', label: 'Keyboard shortcuts', group: 'Global' },
  { id: 'tree_toggle', keys: 'mod+\\', label: 'Collapse or expand the tree', group: 'Global' },
  { id: 'new_task', keys: 'c', label: 'New task', group: 'Create' },
  { id: 'go_home', keys: 'g h', label: 'Home', group: 'Navigate' },
  { id: 'go_my_work', keys: 'g m', label: 'My work', group: 'Navigate' },
  { id: 'go_notifications', keys: 'g n', label: 'Notifications', group: 'Navigate' },
  { id: 'go_team', keys: 'g t', label: 'Team', group: 'Navigate' },
  // PRD-018 v0.4 §3.11: `g w` Work (CFO, Controller; the page reads no-access for the rest).
  { id: 'go_work', keys: 'g w', label: 'Work', group: 'Navigate' },
  // PRD-018 v0.4 §3.11: `g j` Agent jobs (was `g r` for "runs" while the row was Threads; PRD-016 §5 v2.6.2 rename).
  // CFO/Controller only; the chord still fires for a Member and the route gate sends them to My work (gating lives at
  // the route, not the key table, so the `?` sheet stays one table for every role).
  { id: 'go_agent_jobs', keys: 'g j', label: 'Agent jobs', group: 'Navigate' },
  { id: 'period_prev', keys: '[', label: 'Previous month', group: 'Period' },
  { id: 'period_next', keys: ']', label: 'Next month', group: 'Period' },
  { id: 'period_picker', keys: 'mod+shift+p', label: 'Pick a month', group: 'Period' },
  // PRD-018 §3.11 list keys on checklist and My work rows (audit P2-22).
  { id: 'list_down', keys: 'j', label: 'Next row', group: 'Lists' },
  { id: 'list_up', keys: 'k', label: 'Previous row', group: 'Lists' },
  { id: 'list_open', keys: 'enter', label: 'Open the focused row', group: 'Lists' },
  { id: 'list_complete', keys: 'x', label: 'Mark the focused item done', group: 'Lists' },
  { id: 'list_assign', keys: 'a', label: 'Reassign the focused item', group: 'Lists' },
]

/**
 * `g` chord targets (PRD-018 §3.5, §3.11). `g h` and `g m` are both always available: Home is the landing
 * (Q1, Sean 2026-09-11) and My work is one chord away, so the landing choice takes nothing from anyone.
 */
export const CHORDS: Readonly<Record<string, string>> = { h: '/home', m: '/my-work', n: '/my-work/notifications', t: '/team', w: '/work', j: '/agent-jobs' }

export function chordTarget(key: string): string | null {
  return CHORDS[key.toLowerCase()] ?? null
}

export function displayKeys(keys: string): string[] {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  return keys.split(' ').map(part => part.split('+').map((k) => {
    if (k === 'mod')
      return mac ? '⌘' : 'Ctrl'
    if (k === 'shift')
      return '⇧'
    if (k === 'enter')
      return '↵'
    if (k === 'shift+/' || k === '/')
      return k
    return k.length === 1 ? k.toUpperCase() : k
  }).join(mac ? '' : '+')).flatMap((s, i, arr) => i < arr.length - 1 ? [s, 'then'] : [s])
}

export function inTextField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el)
    return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}
